/* @ngInject */

export default (
  helperService,
  liabilitiesService,
  assetFinanceService,
  Asset,
  Liability
) => {
  /**
   * Get available asset types.
   *
   * @returns {Array}
   */
  const getAvailableAssetTypes = () => {
    if (helperService.getOrganizationCountry().cca2 !== 'NZ') {
      let assetTypes = JSON.parse(JSON.stringify(Asset.getAvailableAssetTypes()));
      if (assetTypes && assetTypes.length) {
        const idx = assetTypes.findIndex((a) => a.name === 'kiwisaver');
        if (idx > -1) {
          assetTypes = _.reject(assetTypes, (_, index) => index === idx);
        }
      }
      return assetTypes;
    }
    return Asset.getAvailableAssetTypes();
  };

  /**
   * Get ownership percentage per contact.
   *
   * @param {Boolean} isFirst
   * @param {Array} contacts
   *
   * @returns {Object}
   */
  const getOwnershipPercentagePerContact = (ownership, idContact) => {
    const oP = ownership.find((o) => o.idContact === idContact);
    return oP !== undefined ? helperService.isNumber(oP.percent, false) : 0;
  };

  /**
   * Add asset to assets array.
   *
   * @param {Array} assets
   *
   * @returns {Number}
   */
  const assetAdd = (assets, assetType, contacts) => {
    const ownership = helperService.setOwnershipOfAssetOrLiability(contacts, true);
    assets.push(Asset.blank(assetType, ownership));
    return assets;
  };

  /**
   * Remove asset from assets array.
   *
   * @param {Array} assets
   * @param {Number} index
   *
   * @returns {Array}
   */
  const assetDelete = (assets, index) => helperService.removeItemFromArray(assets, index);

  /**
   * Get total assets per Contact.
   *
   * @param {Array} assets
   * @param {String} idContact
   *
   * @returns {Number}
   */
  const getTotalAssetsPerContact = (assets, idContact) => {
    let total = 0;
    for (let a = 0; a < assets.length; a += 1) {
      const ownership = assets[a].ownership.find((item) => item.idContact === idContact);
      total +=
        ownership && ownership.percent && helperService.isNumber(ownership.percent, true) !== 0
          ? (helperService.isNumber(ownership.percent, true) / 100) *
            helperService.isNumber(assets[a].value, true)
          : 0;
    }
    return total;
  };

  /**
   * Get total assets.
   *
   * @param {Array} assets
   *
   * @returns {Number}
   */
  const getTotalAssets = (assets, assetCommitmentSchedules) => {
    let total = 0;
    for (let a = 0; a < assets.length; a += 1) {
      total += helperService.isNumber(assets[a].value, true);
    }
    if (assetCommitmentSchedules?.length) {
      total += helperService.getAssetCommitmentScheduleTotal({
        assetCommitmentSchedules,
        field: 'currentMarketValue'
      });
    }
    return total;
  };

  /**
   * Get monthly rental income of investment assets per contact.
   *
   * @param {Array} assets
   * @param {String} idContact
   * @param {String} type
   *
   * @returns {Number}
   */
  const getMonthlyRentalIncomeOfInvestmentAssetsPerContact = (assets, idContact, type) => {
    let monthlyRentalIncome = 0;
    const investmentAssets = _.filter(assets, (a) =>
      helperService.isAssetTypeInvestment(a.assetType)
    );
    if (type === 'currentPosition' || type === null) {
      monthlyRentalIncome = investmentAssets.reduce(
        (memo, num) =>
          memo +
          (helperService.isNumber(num.monthlyRentalIncome, false) > 0 &&
          getOwnershipPercentagePerContact(num.ownership, idContact) > 0
            ? (helperService.isNumber(num.monthlyRentalIncome, false) *
                getOwnershipPercentagePerContact(num.ownership, idContact)) /
              100
            : 0),
        0
      );
    } else {
      monthlyRentalIncome = investmentAssets.reduce(
        (memo, num) =>
          memo +
          (helperService.isNumber(num.monthlyRentalIncome, false) > 0 &&
          getOwnershipPercentagePerContact(num.ownership, idContact) > 0 &&
          !num.toBeSold
            ? (helperService.isNumber(num.monthlyRentalIncome, false) *
                getOwnershipPercentagePerContact(num.ownership, idContact)) /
              100
            : 0),
        0
      );
    }
    return monthlyRentalIncome;
  };

  /**
   * Get asset value per ownership percentage.
   *
   * @param {Number} value
   * @param {Number} percent
   *
   * @returns {Number}
   */
  const getAssetValuePerOwnershipPercentage = (value, percent) =>
    value * (helperService.isNumber(percent, false) / 100);

  /**
   * Get available assets.
   *
   * @param {Array} assets
   *
   * @returns {Array}
   */
  const getAvailableAssets = (assets) =>
    _.filter(assets, (a) => a.getName() !== '' && a.value > 0);

  /**
   * Get linked assets.
   *
   * @param {String} liabilityTypeId
   * @param {Array} assets
   *
   * @returns {Array}
   */
  const getLinkedAssets = (liabilityTypeId, assets) => {
    const availableAssets = getAvailableAssets(assets);
    let linkedAssets = [];
    switch (liabilityTypeId) {
      case Liability.getAvailableLiabilityTypes()[0].id:
        linkedAssets = _.filter(availableAssets, (a) =>
          helperService.isAssetTypeOwnerOccupiedOrInvestment(a.assetType)
        );
        break;
      case Liability.getAvailableLiabilityTypes()[3].id:
        linkedAssets = _.filter(availableAssets, (a) =>
          helperService.isAssetTypeVehicle(a.assetType)
        );
        break;
      case Liability.getAvailableLiabilityTypes()[5].id:
        linkedAssets = _.filter(availableAssets, (a) =>
          helperService.isAssetTypeInvestment(a.assetType)
        );
        break;
      default:
        linkedAssets = availableAssets;
        break;
    }
    return linkedAssets;
  };

  return {
    getAvailableAssetTypes,
    getOwnershipPercentagePerContact,
    assetAdd,
    assetDelete,
    getTotalAssetsPerContact,
    getTotalAssets,
    getMonthlyRentalIncomeOfInvestmentAssetsPerContact,
    getAssetValuePerOwnershipPercentage,
    getAvailableAssets,
    getLinkedAssets
  };
};
